import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { card, Btn, Modal, Field, Input, Textarea, Select, ImageUpload, Badge } from "./ui";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";

/* field: {key,label,type: text|textarea|image|select|number|list, options} */
const CONFIGS = {
  packages: {
    title: "Menu Packages", endpoint: "packages",
    fields: [
      { key: "name", label: "Name", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "price_per_person", label: "Price / person (₹)", type: "text" },
      { key: "min_guests", label: "Min Guests", type: "text" },
      { key: "features", label: "Features (comma separated)", type: "list" },
      { key: "image", label: "Image", type: "image" },
      { key: "visible", label: "Visible", type: "bool" },
    ],
    columns: ["name", "price_per_person", "min_guests"],
  },
  gallery: {
    title: "Gallery", endpoint: "gallery",
    fields: [
      { key: "name", label: "Title", type: "text" },
      { key: "category", label: "Category", type: "select", options: ["FOOD", "EVENTS", "BUFFETS", "WEDDINGS", "LIVE COUNTERS", "CORPORATE", "HOSPITALITY"] },
      { key: "image", label: "Image / Video URL", type: "image" },
      { key: "type", label: "Type", type: "select", options: ["image", "video"] },
      { key: "shape", label: "Layout", type: "select", options: ["square", "landscape", "portrait"] },
      { key: "visible", label: "Visible", type: "bool" },
    ],
    columns: ["name", "category", "type"],
  },
  testimonials: {
    title: "Testimonials", endpoint: "testimonials",
    fields: [
      { key: "name", label: "Client Name", type: "text" },
      { key: "role", label: "Role / Event", type: "text" },
      { key: "quote", label: "Testimonial", type: "textarea" },
      { key: "rating", label: "Rating (1-5)", type: "number" },
      { key: "visible", label: "Visible", type: "bool" },
    ],
    columns: ["name", "role", "rating"],
  },
  bulk: {
    title: "Bulk & Food Contracts", endpoint: "bulk",
    fields: [
      { key: "name", label: "Customer", type: "text" },
      { key: "contract", label: "Contract Type", type: "select", options: ["Daily Meals", "Corporate Meals", "Institutional", "Packed Meals", "Recurring", "Office Catering"] },
      { key: "frequency", label: "Meal Frequency", type: "text" },
      { key: "meals", label: "No. of Meals", type: "text" },
      { key: "price", label: "Price", type: "text" },
      { key: "start_date", label: "Start Date", type: "date" },
      { key: "end_date", label: "End Date", type: "date" },
      { key: "location", label: "Delivery Location", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Pending", "Paused", "Completed"] },
    ],
    columns: ["name", "contract", "status"],
  },
  branches: {
    title: "Branches", endpoint: "branches",
    fields: [{ key: "name", label: "Branch Name", type: "text" }, { key: "city", label: "City", type: "text" }, { key: "address", label: "Address", type: "text" }],
    columns: ["name", "city"],
  },
  brands: {
    title: "Brands", endpoint: "brands",
    fields: [{ key: "name", label: "Brand", type: "text" }, { key: "description", label: "Description", type: "textarea" }, { key: "image", label: "Logo", type: "image" }],
    columns: ["name"],
  },
  cctv: {
    title: "CCTV Feeds", endpoint: "cctv",
    fields: [
      { key: "name", label: "Camera Name", type: "text" },
      { key: "area", label: "Area", type: "select", options: ["Kitchen", "Production", "Branch", "Storage", "Service Area", "Event Prep"] },
      { key: "stream_url", label: "Secure Stream URL", type: "text" },
    ],
    columns: ["name", "area"],
  },
};

export default function GenericManager({ type }) {
  const cfg = CONFIGS[type];
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => api.get(`/admin/${cfg.endpoint}`).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, [type]);

  const save = async (item) => {
    const body = { ...item };
    if (item.id) await api.put(`/admin/${cfg.endpoint}/${item.id}`, body);
    else await api.post(`/admin/${cfg.endpoint}`, body);
    setModal(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/${cfg.endpoint}/${id}`); load(); };

  return (
    <div data-testid={`admin-${type}`}>
      <div className="flex items-center justify-between mb-6">
        <div><p className="text-gold/70 text-xs tracking-[0.25em] uppercase">{cfg.title}</p><h1 className="font-serif text-4xl text-ivory mt-1">{cfg.title}</h1></div>
        <Btn onClick={() => setModal({ visible: true })} data-testid={`add-${type}`}><Plus className="w-4 h-4" /> Add</Btn>
      </div>

      {type === "cctv" && <div className={`${card} p-4 mb-4 text-sm text-ivory/60 border-gold/20`}><i className="fa-solid fa-shield-halved text-gold mr-2" /> CCTV feeds are restricted to authorised staff and never exposed on the public website.</div>}

      <div className={`${card} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead><tr className="text-ivory/40 text-left text-xs uppercase tracking-wider border-b border-white/5">
            {cfg.columns.map((c) => <th key={c} className="p-3.5 capitalize">{c.replace(/_/g, " ")}</th>)}
            {cfg.fields.some((f) => f.type === "image") && <th className="p-3.5">Media</th>}
            <th className="p-3.5 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                {cfg.columns.map((c) => <td key={c} className="p-3.5 text-ivory/80">{Array.isArray(r[c]) ? r[c].join(", ") : String(r[c] ?? "—")}</td>)}
                {cfg.fields.some((f) => f.type === "image") && <td className="p-3.5">{r.image ? <img src={r.image} alt="" className="w-10 h-10 rounded object-cover" /> : "—"}</td>}
                <td className="p-3.5">
                  <div className="flex items-center justify-end gap-1">
                    {"visible" in r && <Badge tone={r.visible ? "green" : "gray"}>{r.visible ? "Shown" : "Hidden"}</Badge>}
                    <button onClick={() => setModal(r)} className="text-ivory/40 hover:text-gold p-1.5"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(r.id)} className="text-ivory/40 hover:text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="p-10 text-center text-ivory/40">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <ItemModal cfg={cfg} data={modal} onClose={() => setModal(null)} onSave={save} />
    </div>
  );
}

function ItemModal({ cfg, data, onClose, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(data || {}); }, [data]);
  if (!data) return null;
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <Modal open onClose={onClose} title={f.id ? `Edit ${cfg.title}` : `New ${cfg.title}`}>
      {cfg.fields.map((fld) => {
        if (fld.type === "image") return <ImageUpload key={fld.key} value={f[fld.key]} onChange={(v) => set(fld.key, v)} label={fld.label} />;
        if (fld.type === "bool") return <Field key={fld.key} label={fld.label}><Select value={f[fld.key] ? "1" : "0"} onChange={(e) => set(fld.key, e.target.value === "1")} options={[{ value: "1", label: "Shown" }, { value: "0", label: "Hidden" }]} /></Field>;
        if (fld.type === "select") return <Field key={fld.key} label={fld.label}><Select value={f[fld.key] || ""} onChange={(e) => set(fld.key, e.target.value)} options={["", ...fld.options]} /></Field>;
        if (fld.type === "textarea") return <Field key={fld.key} label={fld.label}><Textarea value={f[fld.key] || ""} onChange={(e) => set(fld.key, e.target.value)} /></Field>;
        if (fld.type === "list") return <Field key={fld.key} label={fld.label}><Textarea value={Array.isArray(f[fld.key]) ? f[fld.key].join(", ") : (f[fld.key] || "")} onChange={(e) => set(fld.key, e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>;
        return <Field key={fld.key} label={fld.label}><Input type={fld.type === "number" ? "number" : fld.type === "date" ? "date" : "text"} value={f[fld.key] || ""} onChange={(e) => set(fld.key, fld.type === "number" ? Number(e.target.value) : e.target.value)} /></Field>;
      })}
      <Btn onClick={() => onSave(f)} className="w-full justify-center mt-2" data-testid={`save-${cfg.endpoint}`}>Save</Btn>
    </Modal>
  );
}
